import { useState } from "react";
import { useParams } from "react-router-dom";
import { ErrorBoundary } from "react-error-boundary";
import { useQuery } from "@tanstack/react-query";
import { apiGet } from "../../api/client";
import { LineageMap } from "../../components/LineageMap";
import { todayInWorkspace } from "../../lib/when";

/**
 * Regulatory memory (NEW-RM) — a lookup, deliberately not a chat.
 *
 * A compliance officer asks two kinds of question and both have exact
 * answers: which instrument was in force on a date, and which rules apply on
 * a date. A box where they typed the question and a model answered would
 * put the model in charge of the most consequential topic in the building —
 * the opposite of what this product claims. So the questions are picked, the
 * dates are stated, and every answer carries its citation and version.
 *
 * Everything on this page is read-only. The key behind it holds the `graph`
 * scope and nothing else.
 */

interface Lineage {
  concept: string;
  label: string;
  name: string;
}

interface InForce {
  id: string;
  name: string;
  version: number;
  effective_from: string;
  effective_to: string | null;
  source_url: string | null;
}

interface RuleView {
  key: string;
  pack: string;
  check_type: string;
  verdict: string;
  declared_verdict: string;
  verdict_capped_by_origin: boolean;
  can_block: boolean;
  origin: string;
  origin_ref: string | null;
  provision_ref_pending: boolean;
  effective_from: string | null;
  summary_en: string;
}

interface RulesResponse {
  as_of: string;
  pack_version: string;
  source: "graphiq" | "local_fallback";
  as_of_honoured: boolean;
  packs: string[];
  rules: RuleView[];
}

function originLabel(origin: string, ref: string | null): string {
  switch (origin) {
    case "statutory":
      return ref ? `Statute · ${ref}` : "Statute";
    case "regulator_rule":
      return ref ? `Regulator · ${ref}` : "Regulator rule";
    case "sharia_ruling":
      return ref ? `Sharia ruling · ${ref}` : "Sharia ruling";
    case "tenant_policy":
      return ref ? `Client policy · ${ref}` : "Client policy (unattributed)";
    default:
      return "Placeholder — not policy, not statute";
  }
}

const INPUT: React.CSSProperties = {};

/** Which instrument was in force on a date — and its whole lineage. */
function AsOfPanel({ workspaceId, lineages }: { workspaceId: string; lineages: Lineage[] }) {
  const [concept, setConcept] = useState("aml.primary_law");
  const [date, setDate] = useState("2026-09-08");
  const [asked, setAsked] = useState<{ concept: string; date: string } | null>(null);

  const asOf = useQuery<{ name: string; as_of: string; in_force: InForce | null }, Error>({
    queryKey: ["reg-asof", workspaceId, asked],
    enabled: !!asked,
    retry: false,
    queryFn: () =>
      apiGet(
        `/api/workspaces/${workspaceId}/regulatory/as-of?concept=${encodeURIComponent(asked!.concept)}&date=${asked!.date}`,
      ),
  });
  const timeline = useQuery<{ versions: (InForce & { current: boolean })[] }, Error>({
    queryKey: ["reg-timeline", workspaceId, asked?.concept],
    enabled: !!asked,
    retry: false,
    queryFn: () =>
      apiGet(`/api/workspaces/${workspaceId}/regulatory/timeline?concept=${encodeURIComponent(asked!.concept)}`),
  });

  return (
    <div className="play-card" data-testid="asof-panel">
      <div className="play-title">Which instrument was in force?</div>
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 8, flexWrap: "wrap" }}>
        <select aria-label="Lineage" value={concept} onChange={(e) => setConcept(e.target.value)} style={INPUT}>
          {lineages.map((l) => (
            <option key={l.concept} value={l.concept}>
              {l.name}
            </option>
          ))}
        </select>
        <input aria-label="On date" value={date} onChange={(e) => setDate(e.target.value)} style={{ ...INPUT, width: 110 }} />
        <button
          type="button"
          className="btn"
          data-testid="asof-ask"
          onClick={() => setAsked({ concept, date })}
        >
          Look up
        </button>
      </div>

      {asOf.isError ? (
        <div className="play-meta" style={{ marginTop: 8 }}>
          <span>{asOf.error.message}</span>
        </div>
      ) : null}

      {asOf.data ? (
        <div style={{ marginTop: 12 }} data-testid="asof-answer">
          <div className="section-eyebrow">ON {asOf.data.as_of}</div>
          {asOf.data.in_force ? (
            <>
              <div className="play-title">{asOf.data.in_force.name}</div>
              <div className="play-meta">
                <span>
                  In force from <span className="v">{asOf.data.in_force.effective_from}</span>
                </span>
                <span>
                  Until{" "}
                  <span className="v">{asOf.data.in_force.effective_to ?? "current"}</span>
                </span>
                {asOf.data.in_force.source_url ? (
                  <a href={asOf.data.in_force.source_url} target="_blank" rel="noreferrer">
                    source
                  </a>
                ) : null}
              </div>
            </>
          ) : (
            // Null is an answer, not an error: nothing was in force then.
            <div className="play-title">Nothing was in force on that date.</div>
          )}
        </div>
      ) : null}

      {timeline.data ? (
        <div style={{ marginTop: 12 }} data-testid="asof-timeline">
          <div className="section-eyebrow">LINEAGE</div>
          {timeline.data.versions.map((v) => (
            <div key={v.id} className="play-meta">
              <span>
                <span className="v">{v.name}</span>
              </span>
              <span>
                {v.effective_from} → {v.effective_to ?? "current"}
              </span>
              {v.current ? <span className="pill green">current</span> : null}
            </div>
          ))}
          <div className="play-meta" style={{ marginTop: 6 }}>
            <span style={{ opacity: 0.7 }}>
              Intervals are half-open: a superseded instrument ends on the day
              its successor begins, with no gap and no overlap.
            </span>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/** Every rule in force on a date, by pack, with its authority stated. */
function RulesPanel({ workspaceId }: { workspaceId: string }) {
  const [date, setDate] = useState("2026-09-08");
  const [pack, setPack] = useState("");
  const [asked, setAsked] = useState("2026-09-08");

  const rules = useQuery<RulesResponse, Error>({
    queryKey: ["reg-rules", workspaceId, asked],
    retry: false,
    queryFn: () => apiGet(`/api/workspaces/${workspaceId}/regulatory/rules?as_of=${asked}`),
  });

  const list = (rules.data?.rules ?? []).filter((r) => !pack || r.pack === pack);
  const blocking = list.filter((r) => r.can_block).length;
  const placeholders = list.filter((r) => r.origin === "xamun_placeholder").length;

  return (
    <div className="play-card" data-testid="rules-panel">
      <div className="play-title">Which rules apply?</div>
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 8, flexWrap: "wrap" }}>
        <input aria-label="Rules as of" value={date} onChange={(e) => setDate(e.target.value)} style={{ ...INPUT, width: 110 }} />
        <select aria-label="Pack" value={pack} onChange={(e) => setPack(e.target.value)} style={INPUT}>
          <option value="">All packs</option>
          {(rules.data?.packs ?? []).map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </select>
        <button
          type="button"
          className="btn"
          data-testid="rules-ask"
          onClick={() => setAsked(date)}
        >
          Show rules
        </button>
      </div>

      {rules.data ? (
        <div className="play-meta" style={{ marginTop: 10 }} data-testid="rules-summary">
          <span>
            In force <span className="v">{list.length}</span>
          </span>
          <span>
            May block <span className="v">{blocking}</span>
          </span>
          <span>
            Placeholders <span className="v">{placeholders}</span>
          </span>
          <span>
            Pack <span className="v">{rules.data.pack_version}</span>
          </span>
          {rules.data.source === "local_fallback" ? (
            <span className="pill amber">offline copy</span>
          ) : null}
          {!rules.data.as_of_honoured ? (
            // Not silence. The list is what the snapshot could see for this
            // date, and it must not be read as the whole set.
            <span className="pill red">may be incomplete for this date</span>
          ) : null}
        </div>
      ) : null}

      <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 8 }}>
        {list.map((r) => (
          <div key={r.key} data-testid={`reg-rule-${r.key}`} style={{ borderTop: "1px solid var(--border)", paddingTop: 8 }}>
            <div className="play-head">
              <div>
                <div className="play-arch">
                  {r.key} · {r.pack}
                </div>
                <div>{r.summary_en}</div>
              </div>
              <div className="play-status">
                <span className={`pill ${r.can_block ? "red" : "amber"}`}>
                  {r.can_block ? "may block" : "warn only"}
                </span>
              </div>
            </div>
            <div className="play-meta">
              <span>{originLabel(r.origin, r.origin_ref)}</span>
              {r.provision_ref_pending ? <span>article reference pending</span> : null}
              {r.effective_from ? (
                <span>
                  from <span className="v">{r.effective_from}</span>
                </span>
              ) : null}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function RegulatoryMemoryInner() {
  const { workspaceId = "" } = useParams();
  const lineages = useQuery<{ lineages: Lineage[]; live: boolean }, Error>({
    queryKey: ["reg-lineages", workspaceId],
    enabled: !!workspaceId,
    queryFn: () => apiGet(`/api/workspaces/${workspaceId}/regulatory/lineages`),
  });

  return (
    <div className="main-inner" data-testid="regulatory-memory">
      <div className="greet-row">
        <span className="greet">Regulatory memory</span>
        {lineages.data ? (
          <span className={`pill ${lineages.data.live ? "green" : "amber"}`}>
            {lineages.data.live ? "live · GraphIQ" : "offline copy"}
          </span>
        ) : null}
      </div>
      <div className="subgreet">
        <div className="section-eyebrow">UAE SAVINGS &amp; FINANCIAL SERVICES</div>
        <div className="lede">
          Ask which instrument was in force on a date, or which rules applied.
          Every answer carries its citation and its version. Nothing here is
          generated.
        </div>
      </div>

      <div className="play-grid">
        <MapPanel workspaceId={workspaceId} live={lineages.data?.live ?? false} />
        <AsOfPanel workspaceId={workspaceId} lineages={lineages.data?.lineages ?? []} />
        <RulesPanel workspaceId={workspaceId} />
      </div>
    </div>
  );
}

/**
 * Every instrument, the version in force on a date, and the rules that cite
 * it — drawn flat. The one genuinely graph-shaped thing on this page, and a
 * small graph, so it is a picture rather than a place to explore.
 */
function MapPanel({ workspaceId, live }: { workspaceId: string; live: boolean }) {
  const [date, setDate] = useState(todayInWorkspace());
  return (
    <div className="play-card" data-testid="map-panel">
      <div className="play-head">
        <div>
          <div className="play-arch">Lineage map</div>
          <div className="play-title">What was in force, and what cites it</div>
        </div>
        <div className="play-status">
          <label>
            as at{" "}
            <input
              aria-label="Map as of"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              style={{ width: 140 }}
            />
          </label>
        </div>
      </div>
      <div style={{ marginTop: 10 }}>
        {live ? (
          <LineageMap workspaceId={workspaceId} asOf={date} />
        ) : (
          <div className="play-meta">
            <span>The map needs GraphIQ live; the offline copy holds rules but not instrument lineages.</span>
          </div>
        )}
      </div>
    </div>
  );
}

export function RegulatoryMemory() {
  return (
    <ErrorBoundary
      fallbackRender={({ error }) => (
        <div className="main-inner" data-testid="regulatory-error">
          <div className="lede">Could not reach regulatory memory: {error.message}</div>
        </div>
      )}
    >
      <RegulatoryMemoryInner />
    </ErrorBoundary>
  );
}
